import { useNavigate } from "react-router-dom";
import {
  LoadingListCards,
  MainButton,
  BodyContainer,
  PrimaryHeading,
  HeaderContainer,
  RightMainContainer,
  ListCard,
  ListIconWrapper,
  TemplateNotFound,
  PopOver,
  PopUpListing,
  AddFromTemplateLibrary,
  Loader,
} from "commonUI";
import { TagsIcon, AddIcon, SettingIcon } from "allIcons";
import { useAccountTags, useTags } from "api/siteSettings/Tags";
import { isEqual, useSearch } from "helpers";
import {
  useAddDefaultTemplateTag,
  useAddTag,
  useDeleteTag,
  useRemoveDefaultTemplateTag,
} from "hooks/tags";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { useState } from "react";
import { ModulePrefixes } from "enums";

export default function Tags({ isSettings }) {
  const [addPopup, setAddPopup] = useState(false);
  const [libraryTemplate, setLibraryTemplate] = useState(false);

  const { web = null } = useSearch();
  const { data: tags, isLoading } = useTags(isSettings || web ? 0 : 1);
  const { data: accontSettingTags, isLoading: loadingAccountTags } =
    useAccountTags();
  const navigate = useNavigate();
  const { mutate: addTag, isLoading: addingTag } = useAddTag(isSettings, false);
  const { mutate: addDefaultTemplateTag, isLoading: addingTagInDefaultTemp } =
    useAddDefaultTemplateTag();
  const {
    mutate: removeDefaultTemplateTag,
    isLoading: removingTagInDefaultTemp,
  } = useRemoveDefaultTemplateTag();

  const deleteTagMutation = useDeleteTag();
  const {
    add_new_items_to_site_template,
    remove_items_from_site_template,
    edit_item_details_in_site_template,
    add_items_from_template_library,
  } = useUserPermissions();

  const getUserAddPermission = () => {
    if (isSettings) {
      return add_new_items_to_site_template;
    } else {
      return add_new_items_to_site_template || add_items_from_template_library;
    }
  };

  return (
    <>
      <RightMainContainer>
        <Loader
          show={addingTagInDefaultTemp || removingTagInDefaultTemp || addingTag}
          loader="block"
        />
        <HeaderContainer>
          <PrimaryHeading>{t("siteSetting.tag.title")}</PrimaryHeading>
          <div className="relative">
            {getUserAddPermission() ? (
              <MainButton
                onClick={() => {
                  if (isSettings) {
                    navigate(`add${web ? "?web=" + web : ""}`);
                  } else {
                    setAddPopup(true);
                  }
                }}
              >
                <AddIcon />
                {t("button.add")}
              </MainButton>
            ) : (
              ""
            )}
            <PopOver
              open={addPopup || libraryTemplate}
              onClose={() => {
                setAddPopup(false);
                setLibraryTemplate(false);
              }}
            >
              {addPopup ? (
                <PopUpListing
                  data={[
                    add_items_from_template_library && {
                      title: t("siteSetting.tag.addFromAccountSettings"),
                      icon: <SettingIcon />,
                      onClick: () => {
                        setLibraryTemplate(true);
                        setAddPopup(false);
                      },
                      arrow: true,
                    },
                    add_new_items_to_site_template && {
                      title: t("siteSetting.tag.createNewTag"),
                      icon: <AddIcon />,
                      onClick: () => {
                        navigate(
                          `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/tags/add${web ? "?web=" + web : ""}`
                        );
                      },
                    },
                  ].filter((_) => _)}
                />
              ) : (
                <AddFromTemplateLibrary
                  isLoading={loadingAccountTags}
                  heading={t("siteSetting.tag.addFromAccountSettings")}
                  data={accontSettingTags
                    ?.filter((_) => {
                      if (!web) return !isEqual(_.system_status, "1");
                      return !tags?.some((ele) => ele.tag_title === _.tag_title);
                    })
                    .map((tag) => ({
                      ...tag,
                      title: tag.tag_title,
                      icon: <TagsIcon />,
                      onClick: () => {
                        if (web) {
                          addTag({
                            tag_title: tag.tag_title,
                            system_status: "0",
                            website_id: web,
                            color: tag.color,
                          });
                        } else {
                          addDefaultTemplateTag(tag.id);
                        }
                      },
                    }))}
                  emptyTemplate={{
                    label: t("siteSetting.tag.title"),
                    linkLabel: t("siteSetting.tag.title"),
                    link: `/account-settings/tags/add${
                      web ? "?web=" + web : ""
                    }`,
                  }}
                />
              )}
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <div className="">
            {isLoading ? (
              <LoadingListCards />
            ) : tags.length === 0 ? (
              <TemplateNotFound
                label={t("siteSetting.tag.title")}
                linkLabel={t("siteSetting.tag.title")}
                filter={false}
                link={
                  add_new_items_to_site_template
                    ? `add${web ? `?web=${web}` : ""}`
                    : ""
                }
              />
            ) : (
              tags?.map((iner, index) => {
                return (
                  <ListCard
                    data={iner.tag_title}
                    key={index}
                    index={index}
                    link={
                      edit_item_details_in_site_template
                        ? isSettings
                          ? `/account-settings/tags/${iner.id}`
                          : `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/tags/${iner.id}${
                              web ? `?web=${web}` : ""
                            }`
                        : ""
                    }
                    editIcon={edit_item_details_in_site_template}
                    deleteIcon={
                      isSettings
                        ? false
                        : !web || !remove_items_from_site_template
                    }
                    crossIcon={
                      !web && remove_items_from_site_template && !isSettings
                    }
                    showModalonCross
                    strictLink={true}
                    onDelete={() => {
                      if (isSettings || web) {
                        deleteTagMutation.mutate(iner.id);
                      } else {
                        removeDefaultTemplateTag(iner.id);
                      }
                    }}
                    icon={
                      <ListIconWrapper>
                        <TagsIcon />
                      </ListIconWrapper>
                    }
                    deleteButtonText={
                      web || isSettings
                        ? t("common.popup.button.confirmDelete")
                        : t("common.deleteButton.title")
                    }
                    deleteTitle={
                      web || isSettings
                        ? t("siteSetting.tag.confirmDelete.forSite.title")
                        : t("siteSetting.tag.confirmDelete.title")
                    }
                    deleteMessage={
                      web || isSettings
                        ? t("siteSetting.tag.confirmDelete.forSite.message", {
                            module: isSettings
                              ? t(
                                  "siteSetting.tag.confirmDelete.module.accountSettings"
                                )
                              : t("siteSetting.tag.confirmDelete.module.site"),
                          })
                        : t("siteSetting.tag.confirmDelete.message")
                    }
                  />
                );
              })
            )}
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
